import { Fragment, type ReactNode } from 'react'

/* ===========================================================================
 *  Markdown, veilig
 *
 *  Voor wat de AI terugschrijft en wat iemand zelf vraagt. Het levert
 *  React-elementen op en nooit HTML-tekst: wat er staat wordt nooit als HTML
 *  geïnterpreteerd, ook niet als het er als HTML uitziet. Dat is hier geen
 *  theorie -- de AI schrijft wat hij wil, en wat hij schrijft komt deels uit
 *  facturen, mail en documenten die van buiten komen. Een factuur met
 *  "<img onerror=...>" in de omschrijving blijft hier gewoon tekst.
 *
 *  Wat het kan: koppen, vet, cursief, code, codeblokken, lijsten, vinkjes,
 *  citaten, links (alleen http, https en mailto), een streep, en tabellen.
 *
 *  Tabellen zijn er voor Cowork bij gekomen: "de openstaande facturen per
 *  bv" is een tabel, en als lijst van dertig regels is hij niet te lezen.
 *  Een kolom met bedragen wordt rechts uitgelijnd met cijfers van gelijke
 *  breedte, zodat de komma's onder elkaar staan -- ook als de AI vergat dat
 *  in de scheidingsregel aan te geven.
 * =========================================================================== */

export function Markdown({ tekst, klein }: { tekst: string; klein?: boolean }) {
  return <div className={`md ${klein ? 'md-klein' : ''}`}>{blokken(tekst ?? '')}</div>
}

/* ------------------------------------------------------------------ *
 *  Tabellen
 * ------------------------------------------------------------------ */

const isTabelRegel = (r: string | undefined) => !!r && /^\s*\|.*\|\s*$/.test(r)
const isScheiding = (r: string | undefined) => !!r && /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(r)

/** Een tabel begint met een rij tussen streepjes, en daaronder |---|---|. */
const tabelBegint = (regels: string[], i: number) => isTabelRegel(regels[i]) && isScheiding(regels[i + 1])

function cellen(r: string): string[] {
  const t = r.trim().replace(/^\|/, '').replace(/\|$/, '')
  /* Een \| in een cel is een streep en geen nieuwe kolom. */
  return t.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'))
}

type Uitlijning = 'links' | 'midden' | 'rechts' | null

function uitlijningen(scheiding: string): Uitlijning[] {
  return cellen(scheiding).map((c) => {
    const l = c.startsWith(':')
    const r = c.endsWith(':')
    return l && r ? 'midden' : r ? 'rechts' : l ? 'links' : null
  })
}

/**
 * Ziet deze cel eruit als een bedrag of een getal? "€ 1.234,56", "-12%",
 * "3", "12,5 uur" niet (dat is tekst). Opmaak als **vet** telt niet mee.
 */
function isGetal(cel: string): boolean {
  const t = cel.replace(/[*_`]/g, '').trim()
  if (!t || t === '-' || t === '–') return false
  return /^[-+−]?\s*(€|EUR)?\s*[-+−]?\s*\d[\d.,\s]*\s*(%|€|EUR|k|mln)?$/i.test(t)
}

function tabel(regels: string[], begin: number): { node: (sleutel: number) => ReactNode; volgende: number } {
  const kop = cellen(regels[begin]!)
  const uit = uitlijningen(regels[begin + 1]!)
  const rijen: string[][] = []
  let i = begin + 2
  while (i < regels.length && isTabelRegel(regels[i])) rijen.push(cellen(regels[i++]!))

  /* Een kolom is een getallenkolom als alle gevulde cellen getallen zijn. */
  const getallen = kop.map((_, k) => {
    const gevuld = rijen.map((r) => r[k] ?? '').filter((x) => x && x !== '-' && x !== '–')
    return gevuld.length > 0 && gevuld.every(isGetal)
  })
  const klasse = (k: number) => {
    const u = uit[k] ?? null
    const kant = u ?? (getallen[k] ? 'rechts' : null)
    return [kant === 'rechts' ? 'rechts' : kant === 'midden' ? 'midden' : '', getallen[k] ? 'getal' : ''].filter(Boolean).join(' ') || undefined
  }

  return {
    volgende: i,
    node: (sleutel) => (
      /* De rol en het label maken het scrolvlak bereikbaar met het
         toetsenbord: een brede tabel op een telefoon scrolt hierbinnen, en
         nooit de hele pagina opzij. */
      <div key={sleutel} className="md-tabel-vak" role="region" aria-label="Tabel" tabIndex={0}>
        <table className="md-tabel">
          <thead>
            <tr>{kop.map((c, k) => <th key={k} className={klasse(k)} scope="col">{inline(c)}</th>)}</tr>
          </thead>
          <tbody>
            {rijen.map((r, n) => (
              <tr key={n}>
                {kop.map((_, k) => <td key={k} className={klasse(k)}>{inline(r[k] ?? '')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  }
}

/* ------------------------------------------------------------------ *
 *  Blokken
 * ------------------------------------------------------------------ */

const LIJST = /^\s*([-*+]|\d+[.)])\s+/

function blokken(tekst: string): ReactNode[] {
  const regels = tekst.replace(/\r\n?/g, '\n').split('\n')
  const uit: ReactNode[] = []
  let i = 0
  let k = 0

  while (i < regels.length) {
    const r = regels[i]!

    /* --- codeblok --- */
    const hek = r.match(/^\s*```(\S*)/)
    if (hek) {
      const code: string[] = []
      i++
      while (i < regels.length && !/^\s*```/.test(regels[i]!)) code.push(regels[i++]!)
      i++
      uit.push(<pre key={k++} className="md-code"><code>{code.join('\n')}</code></pre>)
      continue
    }

    /* --- lege regel --- */
    if (!r.trim()) { i++; continue }

    /* --- tabel --- */
    if (tabelBegint(regels, i)) {
      const t = tabel(regels, i)
      uit.push(t.node(k++))
      i = t.volgende
      continue
    }

    /* --- kop --- */
    const kop = r.match(/^(#{1,6})\s+(.*)$/)
    if (kop) {
      const n = kop[1]!.length
      const inhoud = inline(kop[2]!.replace(/\s+#+\s*$/, ''))
      uit.push(n === 1 ? <h3 key={k++}>{inhoud}</h3> : n === 2 ? <h4 key={k++}>{inhoud}</h4> : <h5 key={k++}>{inhoud}</h5>)
      i++
      continue
    }

    /* --- streep --- */
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(r)) { uit.push(<hr key={k++} />); i++; continue }

    /* --- citaat --- */
    if (/^\s*>/.test(r)) {
      const deel: string[] = []
      while (i < regels.length && /^\s*>/.test(regels[i]!)) deel.push(regels[i++]!.replace(/^\s*>\s?/, ''))
      uit.push(<blockquote key={k++}>{blokken(deel.join('\n'))}</blockquote>)
      continue
    }

    /* --- lijst --- */
    if (LIJST.test(r)) {
      const genummerd = /^\s*\d+[.)]/.test(r)
      const start = genummerd ? Number(r.match(/^\s*(\d+)/)?.[1] ?? 1) : 1
      const punten: ReactNode[] = []
      while (i < regels.length && (LIJST.test(regels[i]!) || /^\s{2,}\S/.test(regels[i]!) && punten.length)) {
        const regel = regels[i]!
        /* Een ingesprongen regel onder een punt hoort bij dat punt: de AI
           zet er graag een toelichting onder. */
        if (!LIJST.test(regel)) {
          punten.push(<li key={`v${i}`} className="md-vervolg">{inline(regel.trim())}</li>)
          i++
          continue
        }
        const ingesprongen = /^\s{2,}/.test(regel)
        const inhoud = regel.replace(LIJST, '')
        const vink = inhoud.match(/^\[( |x|X)\]\s+(.*)$/)
        if (vink) {
          const af = vink[1] !== ' '
          punten.push(
            <li key={i} className={`md-vink ${ingesprongen ? 'md-dieper' : ''}`}>
              <input type="checkbox" checked={af} disabled aria-label={af ? 'Gedaan' : 'Nog niet gedaan'} />
              <span className={af ? 'af' : ''}>{inline(vink[2]!)}</span>
            </li>,
          )
        } else {
          punten.push(<li key={i} className={ingesprongen ? 'md-dieper' : undefined}>{inline(inhoud)}</li>)
        }
        i++
      }
      uit.push(genummerd ? <ol key={k++} start={start !== 1 ? start : undefined}>{punten}</ol> : <ul key={k++}>{punten}</ul>)
      continue
    }

    /* --- alinea: tot de volgende lege regel of het volgende blok --- */
    const alinea: string[] = []
    while (
      i < regels.length && regels[i]!.trim()
      && !/^(#{1,6}\s|\s*```|\s*>|\s*([-*+]|\d+[.)])\s+)/.test(regels[i]!)
      && !tabelBegint(regels, i)
    ) alinea.push(regels[i++]!)
    uit.push(
      <p key={k++}>
        {alinea.map((a, n) => <Fragment key={n}>{n > 0 && <br />}{inline(a)}</Fragment>)}
      </p>,
    )
  }
  return uit
}

/* ------------------------------------------------------------------ *
 *  Binnen een regel
 * ------------------------------------------------------------------ */

/* Volgorde telt: code eerst, want binnen code is een sterretje gewoon een
   sterretje. */
const PATROON = new RegExp([
  '(`[^`]+`)',                                     // code
  '(\\*\\*[^*]+\\*\\*|__[^_]+__)',                // vet
  '(\\*[^*\\s][^*]*\\*|(?<![\\w])_[^_\\s][^_]*_(?![\\w]))', // cursief; een_woord_met_lagen is geen cursief
  '(\\[[^\\]]+\\]\\([^)\\s]+\\))',                 // [tekst](url)
  '(https?:\\/\\/[^\\s<>()]+[^\\s<>().,;:!?\'"])', // los adres
].join('|'), 'g')

/** Alleen http, https en mailto: een javascript:-link uit een factuur wordt nooit klikbaar. */
export function veiligeUrl(u: string | null | undefined): string | null {
  if (!u) return null
  try {
    const url = new URL(u, location.origin)
    return ['http:', 'https:', 'mailto:'].includes(url.protocol) ? url.href : null
  } catch {
    return null
  }
}

function inline(tekst: string): ReactNode[] {
  const uit: ReactNode[] = []
  let laatste = 0
  let k = 0
  for (const m of tekst.matchAll(PATROON)) {
    const begin = m.index ?? 0
    if (begin > laatste) uit.push(tekst.slice(laatste, begin))
    const [heel, code, vet, cursief, link, los] = m

    if (code) uit.push(<code key={k++}>{code.slice(1, -1)}</code>)
    else if (vet) uit.push(<strong key={k++}>{inline(vet.slice(2, -2))}</strong>)
    else if (cursief) uit.push(<em key={k++}>{inline(cursief.slice(1, -1))}</em>)
    else if (link) {
      const lm = link.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/)
      const href = lm ? veiligeUrl(lm[2]!) : null
      uit.push(href
        ? <a key={k++} href={href} target="_blank" rel="noopener noreferrer">{inline(lm![1]!)}</a>
        : <Fragment key={k++}>{lm ? lm[1] : heel}</Fragment>)
    } else if (los) {
      const href = veiligeUrl(los)
      uit.push(href ? <a key={k++} href={href} target="_blank" rel="noopener noreferrer">{los}</a> : los)
    }
    laatste = begin + heel.length
  }
  if (laatste < tekst.length) uit.push(tekst.slice(laatste))
  return uit
}
